// Modal de overrides de config a nivel client.
//
// Cada client de un partner puede overridear un subconjunto de los parametros
// del partner (TTLs, gate de email, plans_enabled, webhook_url). Este modal
// lista TODOS los parametros overrideables en una tabla: por fila, un slider
// que decide si el client overridea o usa el valor del partner, el input del
// valor override, y el valor actual del partner como referencia.
//
// El shape de los overrides es espejo de ClientConfigOverridesSchema del backend
// (libs/common/client-config.ts). Las claves sensibles (require_email_verification,
// plans_enabled) exigen permiso danger_zone — el backend re-verifica; aca solo
// deshabilitamos su edicion como UX.
//
// A11y: <dialog> nativo (focus-trap del browser) + role="switch" en los sliders.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, X, Lock } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import {
  patchClient,
  AdminApiError,
  type AdminClient,
  type ClientConfigOverrides,
  type PartnerDetail,
} from '@/lib/api';
import { fieldCls } from '@/lib/form-styles';

type FieldKind = 'boolean' | 'number' | 'text';
type OverrideKey = keyof ClientConfigOverrides;

interface OverrideField {
  key: OverrideKey;
  label: string;
  hint?: string;
  kind: FieldKind;
  danger: boolean; // requiere danger_zone para cambiarse
  unit?: string;
  min?: number;
  max?: number;
  partnerValue: (p: PartnerDetail) => boolean | number | string | null;
}

// Fuente unica de verdad de los campos overrideables del modal. El orden es el
// orden de las filas.
const FIELDS: OverrideField[] = [
  {
    key: 'require_email_verification',
    label: 'Verificación de email obligatoria',
    hint: 'Si está activo, el login rechaza usuarios con email no verificado.',
    kind: 'boolean',
    danger: true,
    partnerValue: (p) => p.require_email_verification,
  },
  {
    key: 'plans_enabled',
    label: 'Mostrar planes / suscripción',
    hint: 'Habilita el circuito comercial: selección de plan en signup y checkout.',
    kind: 'boolean',
    danger: true,
    partnerValue: (p) => p.features?.['plans_enabled'] === true,
  },
  {
    key: 'access_token_ttl_seconds',
    label: 'Access token TTL',
    hint: 'Vida del access token, en segundos (60 – 86.400).',
    kind: 'number',
    unit: 's',
    min: 60,
    max: 86_400,
    danger: false,
    partnerValue: (p) => p.access_token_ttl_seconds,
  },
  {
    key: 'refresh_token_ttl_seconds',
    label: 'Refresh token TTL',
    hint: 'Vida del refresh token, en segundos (3.600 – 31.536.000).',
    kind: 'number',
    unit: 's',
    min: 3_600,
    max: 31_536_000,
    danger: false,
    partnerValue: (p) => p.refresh_token_ttl_seconds,
  },
  {
    key: 'entitlements_cache_ttl_seconds',
    label: 'Entitlement cache TTL',
    hint: 'TTL del cache de entitlements. Se respeta en paths con contexto de client (/me/*); el path partner-API usa el valor del partner.',
    kind: 'number',
    unit: 's',
    min: 0,
    max: 86_400,
    danger: false,
    partnerValue: (p) => p.entitlements_cache_ttl_seconds,
  },
  {
    key: 'webhook_url',
    label: 'Webhook URL',
    hint: 'Destino de webhooks. Se respeta en eventos con contexto de client (device.authorized); el resto usa el del partner. Vacío = este client no emite webhooks.',
    kind: 'text',
    danger: false,
    partnerValue: (p) => p.webhook_url,
  },
];

interface RowState {
  enabled: boolean;
  value: string | boolean;
}

type Rows = Record<OverrideKey, RowState>;

// ---- helpers de formato / parseo ----------------------------------------

function humanSeconds(n: number): string {
  if (n === 0) return '0 s';
  if (n % 86_400 === 0) return `${n} s (${n / 86_400} d)`;
  if (n % 3_600 === 0) return `${n} s (${n / 3_600} h)`;
  if (n % 60 === 0) return `${n} s (${n / 60} min)`;
  return `${n} s`;
}

function displayValue(f: OverrideField, v: boolean | number | string | null): string {
  if (v === null || v === undefined || v === '') {
    return f.kind === 'text' ? '(sin webhook)' : '—';
  }
  if (f.kind === 'boolean') return v ? 'Sí' : 'No';
  if (f.kind === 'number') return humanSeconds(Number(v));
  return String(v);
}

// Valor inicial de la fila a partir del override guardado del client (o, si no
// hay override, del valor del partner como punto de partida editable).
function initialRows(client: AdminClient, partner: PartnerDetail): Rows {
  const ov = client.config_overrides ?? {};
  const out = {} as Rows;
  for (const f of FIELDS) {
    const has = Object.prototype.hasOwnProperty.call(ov, f.key);
    const partnerVal = f.partnerValue(partner);
    let value: string | boolean;
    if (f.kind === 'boolean') {
      value = has ? Boolean(ov[f.key]) : Boolean(partnerVal);
    } else if (f.kind === 'number') {
      value = has ? String(ov[f.key] ?? '') : String(partnerVal ?? '');
    } else {
      // text (webhook_url): override null → cadena vacía (= sin webhook).
      value = has ? String(ov[f.key] ?? '') : String(partnerVal ?? '');
    }
    out[f.key] = { enabled: has, value };
  }
  return out;
}

// Construye el objeto de overrides que se manda al backend (solo las filas con
// el slider activo). Devuelve también los errores de validación por campo.
function buildOverrides(rows: Rows): {
  overrides: ClientConfigOverrides;
  errors: Partial<Record<OverrideKey, string>>;
} {
  // Record mutable indexado por string: asignar a overrides[unionKey] con la
  // clave tipada como union colapsa el value type a `never` en TS. Casteamos al
  // shape tipado al devolver.
  const overrides: Record<string, unknown> = {};
  const errors: Partial<Record<OverrideKey, string>> = {};
  for (const f of FIELDS) {
    const st = rows[f.key];
    if (!st.enabled) continue;
    if (f.kind === 'boolean') {
      overrides[f.key] = Boolean(st.value);
    } else if (f.kind === 'number') {
      const raw = String(st.value).trim();
      const n = Number(raw);
      if (raw === '' || !Number.isInteger(n)) {
        errors[f.key] = 'Ingresá un entero válido.';
      } else if (f.min !== undefined && n < f.min) {
        errors[f.key] = `Mínimo ${f.min}.`;
      } else if (f.max !== undefined && n > f.max) {
        errors[f.key] = `Máximo ${f.max}.`;
      } else {
        overrides[f.key] = n;
      }
    } else {
      // text: webhook_url. Vacío = null (sin webhook). Si no, validar URL.
      const raw = String(st.value).trim();
      if (raw === '') {
        overrides[f.key] = null;
      } else {
        try {
          // eslint-disable-next-line no-new
          new URL(raw);
          overrides[f.key] = raw;
        } catch {
          errors[f.key] = 'URL inválida.';
        }
      }
    }
  }
  return { overrides: overrides as ClientConfigOverrides, errors };
}

// ---- slider boolean ------------------------------------------------------

function Switch({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-brand-light focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40 ${
        checked ? 'bg-brand' : 'bg-ink-subtle/40 dark:bg-ink-subtle/30'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// ---- modal ---------------------------------------------------------------

export default function ClientOverridesModal({
  open,
  onClose,
  client,
  partner,
  canWrite,
  canEditDanger,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  client: AdminClient;
  partner: PartnerDetail;
  canWrite: boolean;
  canEditDanger: boolean;
  onSaved: () => void;
}): JSX.Element | null {
  const ref = useRef<HTMLDialogElement>(null);
  const [rows, setRows] = useState<Rows>(() => initialRows(client, partner));
  const [confirming, setConfirming] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    if (!open && dlg.open) dlg.close();
  }, [open]);

  // Reset del form cada vez que se (re)abre o cambian los datos del client.
  useEffect(() => {
    if (open) {
      setRows(initialRows(client, partner));
      setConfirming(false);
      setSaveError(null);
    }
  }, [open, client, partner]);

  const { overrides: nextOverrides, errors } = useMemo(() => buildOverrides(rows), [rows]);
  const hasErrors = Object.keys(errors).length > 0;

  const currentNormalized = JSON.stringify(client.config_overrides ?? {});
  const nextNormalized = JSON.stringify(nextOverrides);
  const dirty = currentNormalized !== nextNormalized;

  function setRow(key: OverrideKey, patch: Partial<RowState>): void {
    setRows((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  async function doSave(): Promise<void> {
    setSaving(true);
    setSaveError(null);
    try {
      await patchClient(client.client_id, { config_overrides: nextOverrides });
      setConfirming(false);
      onSaved();
      onClose();
    } catch (e) {
      setSaveError(e instanceof AdminApiError ? e.message : 'Error guardando los overrides.');
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  }

  // Filas activas (para el resumen del confirm): parámetro, valor partner → override.
  const activeSummary = FIELDS.filter((f) => rows[f.key].enabled).map((f) => {
    const rawOverride = (nextOverrides as Record<string, unknown>)[f.key];
    return {
      key: f.key,
      label: f.label,
      partner: displayValue(f, f.partnerValue(partner)),
      override: displayValue(
        f,
        rawOverride === undefined ? null : (rawOverride as boolean | number | string | null),
      ),
    };
  });

  return (
    <>
      <dialog
        ref={ref}
        aria-labelledby="overrides-title"
        onClose={onClose}
        className="rounded-xl p-0 w-full max-w-3xl bg-surface dark:bg-surface-dark text-ink dark:text-ink-inverted backdrop:bg-black/50"
      >
        <div className="flex items-center justify-between border-b border-surface-border dark:border-surface-dark-border px-6 py-4">
          <div>
            <h2 id="overrides-title" className="text-lg font-semibold">
              Overrides del client
            </h2>
            <p className="text-xs font-mono text-ink-muted">{client.client_id}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="rounded p-1 text-ink-muted hover:bg-surface-muted dark:hover:bg-surface-dark-muted"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
          <p className="text-sm text-ink-muted">
            Activá el slider para que este client use un valor propio en vez del valor del
            partner. Slider apagado = usa el valor del partner.
          </p>

          <div className="overflow-x-auto rounded-lg border border-surface-border dark:border-surface-dark-border">
            <table className="w-full min-w-[40rem] text-sm">
              <thead>
                <tr className="border-b border-surface-border dark:border-surface-dark-border text-left text-xs uppercase text-ink-muted">
                  <th className="p-2 font-medium">Parámetro</th>
                  <th className="p-2 font-medium w-24 text-center">Override</th>
                  <th className="p-2 font-medium">Valor del override</th>
                  <th className="p-2 font-medium">Valor del partner</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border dark:divide-surface-dark-border">
                {FIELDS.map((f) => {
                  const st = rows[f.key];
                  const locked = f.danger && !canEditDanger;
                  const disabled = !canWrite || locked;
                  const partnerVal = f.partnerValue(partner);
                  return (
                    <tr key={f.key} className={st.enabled ? 'bg-brand/5' : undefined}>
                      <td className="p-2 align-top">
                        <div className="flex items-center gap-1 font-medium">
                          {f.label}
                          {f.danger ? (
                            <span
                              title="Requiere permiso danger_zone"
                              className="text-status-warning"
                            >
                              <Lock size={11} aria-hidden="true" />
                            </span>
                          ) : null}
                        </div>
                        {f.hint ? (
                          <p className="text-[0.7rem] leading-snug text-ink-muted mt-0.5 max-w-xs">
                            {f.hint}
                          </p>
                        ) : null}
                      </td>
                      <td className="p-2 align-top text-center">
                        <Switch
                          checked={st.enabled}
                          disabled={disabled}
                          onChange={(v) => setRow(f.key, { enabled: v })}
                          label={`Override de ${f.label}`}
                        />
                      </td>
                      <td className="p-2 align-top">
                        {f.kind === 'boolean' ? (
                          <label
                            className={`inline-flex items-center gap-2 text-xs ${
                              st.enabled ? '' : 'opacity-40'
                            }`}
                          >
                            <Switch
                              checked={Boolean(st.value)}
                              disabled={disabled || !st.enabled}
                              onChange={(v) => setRow(f.key, { value: v })}
                              label={`Valor de ${f.label}`}
                            />
                            <span>{st.value ? 'Sí' : 'No'}</span>
                          </label>
                        ) : (
                          <div>
                            <input
                              type={f.kind === 'number' ? 'number' : 'text'}
                              inputMode={f.kind === 'number' ? 'numeric' : undefined}
                              min={f.min}
                              max={f.max}
                              value={String(st.value)}
                              disabled={disabled || !st.enabled}
                              placeholder={f.kind === 'text' ? 'https://… (vacío = sin webhook)' : ''}
                              onChange={(e) => setRow(f.key, { value: e.target.value })}
                              className={fieldCls({
                                size: 'sm',
                                mono: f.kind === 'number',
                                invalid: Boolean(errors[f.key]),
                              })}
                            />
                            <div className="flex items-center gap-2 mt-0.5">
                              {f.unit && st.enabled ? (
                                <span className="text-[0.65rem] text-ink-subtle">
                                  en {f.unit === 's' ? 'segundos' : f.unit}
                                </span>
                              ) : null}
                              {errors[f.key] ? (
                                <span className="text-[0.65rem] text-status-error">
                                  {errors[f.key]}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="p-2 align-top text-ink-muted">
                        <span className="text-xs">{displayValue(f, partnerVal)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!canEditDanger ? (
            <p className="text-[0.7rem] text-ink-muted flex items-center gap-1">
              <Lock size={11} aria-hidden="true" />
              Los parámetros marcados requieren el permiso danger_zone para cambiarse.
            </p>
          ) : null}

          {saveError ? <p className="text-sm text-status-error">{saveError}</p> : null}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-surface-border dark:border-surface-dark-border px-6 py-4">
          <span className="text-xs text-ink-muted">
            {dirty ? 'Hay cambios sin guardar.' : 'Sin cambios.'}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="min-h-[2.75rem] rounded-lg border border-surface-border dark:border-surface-dark-border px-4 py-2 text-sm hover:bg-surface-muted dark:hover:bg-surface-dark-muted"
            >
              Descartar
            </button>
            <button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={!canWrite || !dirty || hasErrors}
              className="min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 disabled:opacity-50"
            >
              Guardar cambios
            </button>
          </div>
        </div>
      </dialog>

      <ConfirmDialog
        open={confirming}
        title="¿Está usted seguro de que quiere hacer estos cambios?"
        confirmLabel="Sí, guardar"
        loading={saving}
        onConfirm={() => void doSave()}
        onCancel={() => setConfirming(false)}
        description={
          <div className="space-y-3">
            {activeSummary.length === 0 ? (
              <p>
                Se quitan <strong>todos</strong> los overrides: este client pasará a usar los
                valores del partner en todos los parámetros.
              </p>
            ) : (
              <>
                <p>
                  El client <span className="font-mono">{client.client_id}</span> quedará con estos
                  overrides (valor del partner → valor del override):
                </p>
                <ul className="space-y-1">
                  {activeSummary.map((s) => (
                    <li
                      key={s.key}
                      className="flex flex-wrap items-center gap-2 rounded border border-surface-border dark:border-surface-dark-border px-2 py-1 text-xs"
                    >
                      <span className="font-medium">{s.label}:</span>
                      <span className="text-ink-muted line-through decoration-ink-subtle/50">
                        {s.partner}
                      </span>
                      <ArrowRight size={13} aria-hidden="true" className="text-brand" />
                      <span className="font-medium text-brand">{s.override}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        }
      />
    </>
  );
}
